import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Alert, Spinner, Dropdown, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlus, FaEdit, FaTrash, FaEye, FaEllipsisV, FaSearch, FaFilter, FaBox, FaChartLine, FaTag, FaLayerGroup, FaThLarge, FaList } from 'react-icons/fa';
import SearchBar from '../../components/common/SearchBar';
import Layout from '../../components/layout/Layout';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './Products.css';

// Helper function to handle image errors
const handleImageError = (e) => {
  // Replace with picsum photos when image fails to load
  const productName = e.target.alt || 'product';
  const encodedName = encodeURIComponent(productName.replace(/\s+/g, '+'));
  e.target.src = `https://picsum.photos/seed/${encodedName}/400/300`;
};

const SellerProducts = () => {
  const { currentUser } = useAuth();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortKey, setSortKey] = useState('dateAdded_desc');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  const hasPendingProducts = React.useMemo(
    () => products.some(p => p?.approval_status === 'pending'),
    [products]
  );
  const hasRejectedProducts = React.useMemo(
    () => products.some(p => p?.approval_status === 'rejected'),
    [products]
  );
  
  useEffect(() => {
    setLoading(true);
    const fetchProducts = async () => {
      try {
        const response = await api.get('/products/', {
          params: {
            mine: true,
            ...(searchTerm ? { search: searchTerm } : {}),
          },
        });
        const apiProducts = response.data;
        console.log('API products:', apiProducts);
        console.log('Current user ID:', currentUser?.id);
        if (apiProducts.length > 0) {
          console.log('Types:', typeof apiProducts[0]?.seller, typeof currentUser?.id);
        }
        const productsArray = Array.isArray(apiProducts.results) ? apiProducts.results : [];
        setProducts(productsArray);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load products');
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [currentUser?.id, searchTerm]);
  
  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    setSuccess('');
    setError('');
    setLoading(true);

    try {
      await api.delete(`/products/${productId}/`);
      setProducts(prev => prev.filter(p => p.id !== productId));
      setSuccess('Product deleted successfully.');
    } catch (err) {
      console.error('Error deleting product:', err);
      setError('Failed to delete product.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleToggleStatus = async (productId) => {
    setSuccess('');
    setError('');
    setLoading(true);
    
    const product = products.find(p => p.id === productId);
    if (!product) {
      setError('Product not found');
      setLoading(false);
      return;
    }

    if (product.approval_status && product.approval_status !== 'approved') {
      setError('This product cannot be shown/hidden until it has been approved by an admin.');
      setLoading(false);
      return;
    }

    const newStatus = product.status === 'active' ? 'hidden' : 'active';

    try {
      await api.patch(`/products/${productId}/`, { status: newStatus });
      setProducts(prev => prev.map(p => (
        p.id === productId ? { ...p, status: newStatus } : p
      )));
      setSuccess(`Product status changed to ${newStatus}.`);
    } catch (err) {
      console.error('Error updating product status:', err);
      setError('Failed to update product status.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'sold':
        return 'secondary';
      case 'hidden':
        return 'warning';
      case 'suspended':
        return 'danger';
      default:
        return 'light';
    }
  };

  // Remove the in-memory search filter from filteredAndSortedProducts (searchTerm is now handled by backend)
  const filteredAndSortedProducts = React.useMemo(() => {
    let result = [...products];

    if (categoryFilter) {
      result = result.filter(p => p.category === categoryFilter);
    }
    if (statusFilter) {
      result = result.filter(p => p.status === statusFilter);
    }

    const [key, direction] = sortKey.split('_');
    result.sort((a, b) => {
      let valA = a[key];
      let valB = b[key];

      if (key === 'price' || key === 'stock' || key === 'sales') {
        valA = Number(valA);
        valB = Number(valB);
      }

      if (valA < valB) return direction === 'asc' ? -1 : 1;
      if (valA > valB) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [products, categoryFilter, statusFilter, sortKey]);

  const handleSearch = (searchTerm = '') => {
    setSearchTerm(searchTerm);
    // The actual search is handled by the useEffect hook
  };

  const getPlainTextDescription = (html) => {
    if (!html || typeof html !== 'string') return '';
    try {
      const withoutTags = html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ');
      return withoutTags.replace(/\s+/g, ' ').trim();
    } catch (e) {
      return html;
    }
  };

  const getFeaturesArray = (features) => {
    if (!features) return [];
    if (Array.isArray(features)) return features.filter(Boolean);
    if (typeof features === 'string') {
      try {
        const parsed = JSON.parse(features);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100
      }
    }
  };

  return (
    <Layout>
      <div className="seller-products-container">
        <Container className="py-4">
          {/* Modern Header */}
          <motion.div 
            className="products-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h1 className="products-title">My Products</h1>
                <p className="products-subtitle">
                  Manage your {filteredAndSortedProducts.length} product{filteredAndSortedProducts.length !== 1 ? 's' : ''}
                </p>
              </div>
              <Button 
                as={Link} 
                to="/seller/add-product"
                className="add-product-btn"
                size="lg"
              >
                <FaPlus className="me-2" />
                Add Product
              </Button>
            </div>
          </motion.div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

          {hasPendingProducts && (
            <Alert variant="warning" dismissible className="mb-3">
              Some of your products are pending approval. They will not be visible on the marketplace until an admin approves them.
            </Alert>
          )}
          {hasRejectedProducts && (
            <Alert variant="danger" dismissible className="mb-3">
              Some of your products were rejected. Please edit them and resubmit, or contact support if you believe this is a mistake.
            </Alert>
          )}

          {/* Filter Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="filter-card shadow-sm mb-4">
              <Card.Body>
                <Row className="g-3 align-items-center">
                  <Col lg={5}>
                    <SearchBar 
                      placeholder="Search your products..."
                      value={searchTerm}
                      onChange={setSearchTerm}
                      onSubmit={handleSearch}
                      className="w-100"
                    />
                  </Col>
                  <Col lg={2}>
                    <Form.Select 
                      value={categoryFilter} 
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      className="filter-select"
                    >
                      <option value="">All Categories</option>
                      {[...new Set(products.map(p => p.category))].map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </Form.Select>
                  </Col>
                  <Col lg={2}>
                    <Form.Select 
                      value={statusFilter} 
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="filter-select"
                    >
                      <option value="">All Statuses</option>
                      <option value="active">Active</option>
                      <option value="sold">Sold</option>
                      <option value="hidden">Hidden</option>
                    </Form.Select>
                  </Col>
                  <Col lg={2}>
                    <Form.Select 
                      value={sortKey} 
                      onChange={(e) => setSortKey(e.target.value)}
                      className="filter-select"
                    >
                      <option value="dateAdded_desc">Newest First</option>
                      <option value="dateAdded_asc">Oldest First</option>
                      <option value="price_asc">Price: Low to High</option>
                      <option value="price_desc">Price: High to Low</option>
                      <option value="sales_desc">Top Selling</option>
                    </Form.Select>
                  </Col>
                  <Col lg={1} className="text-end">
                    <div className="view-toggle">
                      <button 
                        className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                        onClick={() => setViewMode('grid')}
                        title="Grid View"
                      >
                        <FaThLarge />
                      </button>
                      <button 
                        className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                        onClick={() => setViewMode('list')}
                        title="List View"
                      >
                        <FaList />
                      </button>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </motion.div>

          {/* Products Grid/List */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading your products...</p>
            </div>
          ) : filteredAndSortedProducts.length === 0 ? (
            <motion.div 
              className="empty-state"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <FaBox className="empty-icon" />
              <h3>No Products Found</h3>
              <p>Start by adding your first product to your store</p>
              <Button as={Link} to="/seller/add-product" className="add-first-btn">
                <FaPlus className="me-2" />
                Add Your First Product
              </Button>
            </motion.div>
          ) : (
            <motion.div 
              className={viewMode === 'grid' ? 'product-grid' : 'product-list'}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {filteredAndSortedProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  variants={itemVariants}
                  whileHover={{ y: -5 }}
                >
                  <Card className="product-card-seller shadow-sm">
                    <div className="product-image-wrapper seller-product-image-wrapper">
                      <img
                        src={product.images && product.images.length > 0 ? product.images[0].image : `https://placehold.co/400x300?text=No+Image`}
                        alt={product.name}
                        onError={handleImageError}
                        className="product-image-fresh seller-product-image"
                      />
                    </div>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <Card.Title className="product-name">{product.name}</Card.Title>
                        <div className="d-flex gap-2 flex-wrap justify-content-end">
                          <Badge bg={getStatusBadge(product.status)} className="status-badge">
                            {product.status}
                          </Badge>
                          {product.approval_status && product.approval_status !== 'approved' && (
                            <Badge bg={product.approval_status === 'rejected' ? 'danger' : 'warning'} text={product.approval_status === 'pending' ? 'dark' : undefined}>
                              {product.approval_status}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="product-meta">
                        <span className="meta-item">
                          <FaTag className="me-1" />
                          {product.category}
                        </span>
                        <span className="meta-divider">·</span>
                        <span className="meta-item">{product.condition}</span>
                      </div>
                      <div className="product-price">
                        GH₵ {Number(product.price).toFixed(2)}
                      </div>
                      {getPlainTextDescription(product.description) && (
                        <div className="product-description">
                          {(() => {
                            const text = getPlainTextDescription(product.description);
                            return text.length > 90 ? `${text.slice(0, 90)}...` : text;
                          })()}
                        </div>
                      )}
                      {getFeaturesArray(product.features).length > 0 && (
                        <div className="product-features">
                          {getFeaturesArray(product.features).slice(0, 3).map((feature, idx) => (
                            <span key={idx} className="feature-pill">{feature}</span>
                          ))}
                        </div>
                      )}
                      <div className="product-stats">
                        <div className="stat-item">
                          <FaBox className="stat-icon" />
                          <span>Stock: {product.stock || 0}</span>
                        </div>
                        <div className="stat-item">
                          <FaChartLine className="stat-icon" />
                          <span>Sales: {product.sales || 0}</span>
                        </div>
                      </div>
                    </Card.Body>
                    <Card.Footer className="product-footer">
                      <div className="footer-actions">
                        <Button 
                          as={Link} 
                          to={`/seller/products/edit/${product.id}`}
                          variant="outline-primary"
                          size="sm"
                          className="action-btn"
                        >
                          <FaEdit className="me-1" /> Edit
                        </Button>
                        <Dropdown align="end">
                          <Dropdown.Toggle 
                            as={Button} 
                            variant="outline-secondary" 
                            size="sm"
                            className="action-btn"
                          >
                            <FaEllipsisV />
                          </Dropdown.Toggle>
                          <Dropdown.Menu className="dropdown-menu-modern">
                            <Dropdown.Item
                              onClick={() => handleToggleStatus(product.id)}
                              disabled={product.approval_status && product.approval_status !== 'approved'}
                            >
                              {product.approval_status && product.approval_status !== 'approved'
                                ? 'Awaiting approval'
                                : product.status === 'active'
                                  ? '🙈 Hide Product'
                                  : '👁️ Show Product'
                              }
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item 
                              onClick={() => handleDeleteProduct(product.id)} 
                              className="text-danger"
                            >
                              <FaTrash className="me-2"/> Delete Product
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </Card.Footer>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          )}
        </Container>
      </div>
    </Layout>
  );
};

export default SellerProducts;
